import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, Zap, TrendingUp, Clock, Brain, BarChart3, ArrowRight, Send, MessageCircle, Twitter, Facebook, Instagram, Youtube, Lock, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const KEYFRAMES = `
  @keyframes floatA { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-28px) scale(1.04)} }
  @keyframes floatB { 0%,100%{transform:translateY(-8px) rotate(0deg)} 50%{transform:translateY(10px) rotate(5deg)} }
  @keyframes floatC { 0%,100%{transform:translateY(-14px)} 50%{transform:translateY(14px)} }
  @keyframes rippleOut { 0%{transform:scale(0);opacity:0.55} 100%{transform:scale(5);opacity:0} }
  @keyframes pulseRing { 0%{transform:scale(1);opacity:0.5} 80%{transform:scale(1.8);opacity:0} 100%{transform:scale(1.8);opacity:0} }
  @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
  @keyframes gradientShift { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
  @keyframes borderPulse { 0%,100%{border-color:rgba(56,254,96,0.15)} 50%{border-color:rgba(56,254,96,0.4)} }
  @keyframes iconBounce { 0%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} 60%{transform:translateY(-3px)} }
`;

function useCounter(target, duration = 2000, started = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!started) return;
    let id;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      setVal(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [target, duration, started]);
  return val;
}

function useVisible(thresh = 0.1) {
  const [v, setV] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setV(true), { threshold: thresh });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, v];
}

const FadeIn = ({ children, delay = 0, className = '', style: extraStyle = {} }) => {
  const [ref, vis] = useVisible(0.08);
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? 'none' : 'translateY(24px)',
      transition: `opacity 0.75s ${delay}ms ease, transform 0.75s ${delay}ms ease`,
      ...extraStyle,
    }}>
      {children}
    </div>
  );
};

const CARD = {
  bg: 'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.12)',
  borderHov: 'rgba(56,254,96,0.3)',
  radius: 22,
};

const PHILOSOPHY = [
  { icon: Clock,    title: 'Technology Over Emotion',  desc: 'Markets never sleep, but humans must. We build software that operates on pure logic and mathematics — eliminating fear and greed from every trade.', tag: '24/7 Automated Execution', color: '#38FE60', rgb: '56,254,96' },
  { icon: Shield,   title: 'Security by Design',       desc: 'We operate on a strict non-custodial model. Your funds never leave your exchange. We only use read/write API keys — never withdrawal permissions.',  tag: 'Non-Custodial Model',      color: '#38bdf8', rgb: '56,189,248' },
  { icon: Brain,    title: 'Education First',           desc: 'We believe informed traders are better traders. Every feature ships with documentation, tutorials, and backtest data so you understand the why.',     tag: 'Backtested Strategies',    color: '#a78bfa', rgb: '167,139,250' },
  { icon: TrendingUp, title: 'Multi-Asset Agility',    desc: 'One unified interface for Crypto, Forex, Stocks, and Commodities. Trade any asset on any exchange without switching tools.',                         tag: '1,000+ Trading Pairs',     color: '#fbbf24', rgb: '251,191,36' },
  { icon: Zap,      title: 'Seamless Integration',     desc: 'We specialise in the TradingView-to-Exchange pipeline. Turn any Pine Script alert into a live order in milliseconds with no code required.',         tag: 'Zero-Code Automation',     color: '#38FE60', rgb: '56,254,96' },
  { icon: BarChart3, title: 'Institutional-Grade Logic', desc: 'The same automation tooling used by large-scale trading desks — now accessible to every individual trader on the platform.',                       tag: 'Low-Latency Execution',    color: '#38bdf8', rgb: '56,189,248' },
];

const SOCIALS = [
  { icon: Send,          label: 'Telegram',    sub: 'Real-time signals',    href: 'https://t.me/bullexailabs',                                color: '#38FE60', rgb: '56,254,96'   },
  { icon: Youtube,       label: 'YouTube',     sub: 'Tutorials & insights', href: 'https://www.youtube.com/@bullexailabs/videos',             color: '#ff453a', rgb: '255,69,58'   },
  { icon: Twitter,       label: 'Twitter / X', sub: 'Market updates',       href: 'https://x.com/BullexAiLabs',                              color: '#fff',    rgb: '224,224,224' },
  { icon: Instagram,     label: 'Instagram',   sub: 'Daily tips',           href: 'https://www.instagram.com/bullexailabs/',                  color: '#f472b6', rgb: '244,114,182' },
  { icon: Facebook,      label: 'Facebook',    sub: 'Announcements',        href: 'https://www.facebook.com/profile.php?id=61583845973970',  color: '#60a5fa', rgb: '96,165,250'  },
  { icon: MessageCircle, label: 'Discord',     sub: 'Community chat',       href: 'https://discord.gg/shdwXNAX',                             color: '#a78bfa', rgb: '167,139,250' },
];

const TICKER_ITEMS = ['TradingView Webhooks', 'Non-Custodial', 'Low-Latency Execution', 'Backtested Strategies', '24/7 Automation', 'Multi-Exchange', 'Pine Script Alerts', 'Risk Management', 'Algorithmic Trading', '1000+ Pairs'];

const ScrollProgress = () => {
  const [prog, setProg] = useState(0);
  useEffect(() => {
    const fn = () => {
      const el = document.documentElement;
      setProg((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100 || 0);
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <div style={{ position: 'fixed', top: 68, left: 0, right: 0, height: 2, zIndex: 49, background: 'rgba(56,254,96,0.08)' }}>
      <div style={{ height: '100%', width: `${prog}%`, background: 'linear-gradient(90deg,#38FE60,#38bdf8)', boxShadow: '0 0 8px rgba(56,254,96,0.7)', transition: 'width 0.08s linear' }} />
    </div>
  );
};

const TickerStrip = () => (
  <div style={{ overflow: 'hidden', marginBottom: 72, borderRadius: 12, background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', padding: '14px 0', userSelect: 'none' }}>
    <div style={{ display: 'flex', width: 'max-content', animation: 'ticker 30s linear infinite' }}>
      {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
        <span key={i} style={{ padding: '0 28px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 28 }}>
          <span style={{ color: i % 3 === 0 ? '#38FE60' : 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{t}</span>
          <span style={{ color: 'rgba(255,255,255,0.12)', fontSize: 8 }}>◆</span>
        </span>
      ))}
    </div>
  </div>
);

const StatCounter = ({ target, suffix, label, color, rgb, started }) => {
  const val = useCounter(target, 1800, started);
  return (
    <div style={{ textAlign: 'center', padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <p style={{ color: '#fff', fontSize: 'clamp(1.8rem,3.8vw,2.6rem)', fontWeight: 800, letterSpacing: '-0.045em', lineHeight: 1 }}>
        {val.toLocaleString()}{suffix}
      </p>
      <p style={{ color: '#bbb', fontSize: 12, marginBottom: 10 }}>{label}</p>
      <div style={{ width: 32, height: 3, borderRadius: 2, background: color, boxShadow: `0 0 8px rgba(${rgb},0.6)` }} />
    </div>
  );
};

const PhilosophyCard = ({ item, delay }) => {
  const [ref, vis] = useVisible(0.08);
  const [hov, setHov] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [iconAnim, setIconAnim] = useState(false);
  const Icon = item.icon;

  const addRipple = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const id = Date.now();
    setRipples(r => [...r, { x: e.clientX - rect.left, y: e.clientY - rect.top, id }]);
    setTimeout(() => setRipples(r => r.filter(rip => rip.id !== id)), 750);
  };

  return (
    <div ref={ref}
      onMouseEnter={() => { setHov(true); setIconAnim(true); setTimeout(() => setIconAnim(false), 600); }}
      onMouseLeave={() => setHov(false)}
      onClick={addRipple}
      style={{
        borderRadius: CARD.radius,
        padding: '32px 28px',
        background: hov ? `linear-gradient(145deg, rgba(${item.rgb},0.06) 0%, rgba(255,255,255,0.03) 100%)` : CARD.bg,
        border: `1px solid ${hov ? `rgba(${item.rgb},0.35)` : CARD.border}`,
        boxShadow: hov ? `0 20px 56px rgba(0,0,0,0.4), 0 0 0 1px rgba(${item.rgb},0.08)` : 'none',
        transform: vis ? (hov ? 'translateY(-6px) scale(1.01)' : 'none') : 'translateY(22px)',
        opacity: vis ? 1 : 0,
        transition: `opacity 0.7s ${delay}ms ease, transform 0.3s ease, border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease`,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}>
      {ripples.map(rip => (
        <span key={rip.id} style={{
          position: 'absolute', borderRadius: '50%',
          width: 180, height: 180,
          left: rip.x - 90, top: rip.y - 90,
          background: `rgba(${item.rgb},0.12)`,
          animation: 'rippleOut 0.75s ease forwards',
          pointerEvents: 'none',
        }} />
      ))}
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: `rgba(${item.rgb},0.1)`,
        border: `1px solid rgba(${item.rgb},0.22)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: item.color, marginBottom: 20, flexShrink: 0,
        transition: 'box-shadow 0.25s ease',
        boxShadow: hov ? `0 0 24px rgba(${item.rgb},0.3)` : 'none',
        animation: iconAnim ? 'iconBounce 0.6s ease' : 'none',
      }}>
        <Icon style={{ width: 22, height: 22 }} />
      </div>
      <p style={{ color: '#fff', fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 10 }}>
        {item.title}
      </p>
      <p style={{ color: '#ccc', fontSize: 14, lineHeight: 1.8, flex: 1, marginBottom: 18 }}>
        {item.desc}
      </p>
      <span style={{
        display: 'inline-block',
        padding: '4px 10px', borderRadius: 6,
        background: `rgba(${item.rgb},0.08)`,
        border: `1px solid rgba(${item.rgb},0.18)`,
        color: item.color, fontSize: 10, fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        width: 'fit-content',
      }}>
        {item.tag}
      </span>
    </div>
  );
};

const SocialCard = ({ s, delay }) => {
  const [ref, vis] = useVisible(0.08);
  const [hov, setHov] = useState(false);
  const Icon = s.icon;
  return (
    <a ref={ref} href={s.href} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 16,
        borderRadius: 18, padding: '20px 22px',
        background: hov ? `rgba(${s.rgb},0.05)` : CARD.bg,
        border: `1px solid ${hov ? `rgba(${s.rgb},0.3)` : CARD.border}`,
        textDecoration: 'none',
        transform: vis ? (hov ? 'translateY(-4px) scale(1.01)' : 'none') : 'translateY(18px)',
        opacity: vis ? 1 : 0,
        boxShadow: hov ? `0 14px 40px rgba(0,0,0,0.35), 0 0 20px rgba(${s.rgb},0.08)` : 'none',
        transition: `opacity 0.65s ${delay}ms ease, transform 0.25s ease, border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease`,
      }}>
      <div style={{
        width: 46, height: 46, borderRadius: 13, flexShrink: 0,
        background: `rgba(${s.rgb},0.1)`,
        border: `1px solid rgba(${s.rgb},0.22)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: s.color,
        boxShadow: hov ? `0 0 20px rgba(${s.rgb},0.3)` : 'none',
        transition: 'box-shadow 0.22s ease, transform 0.22s ease',
        transform: hov ? 'scale(1.12) rotate(-4deg)' : 'scale(1) rotate(0deg)',
      }}>
        <Icon style={{ width: 18, height: 18 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{s.label}</p>
        <p style={{ color: '#bbb', fontSize: 12 }}>{s.sub}</p>
      </div>
      <ChevronRight style={{ width: 14, height: 14, color: hov ? s.color : '#333', transition: 'color 0.2s, transform 0.2s', transform: hov ? 'translateX(4px)' : 'none', flexShrink: 0 }} />
    </a>
  );
};

const AboutUs = () => {
  const navigate = useNavigate();
  const [statsRef, statsVis] = useVisible(0.2);
  const heroRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [mouseIn, setMouseIn] = useState(false);

  const handleMouseMove = useCallback((e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    setMouse({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#111113', color: '#fff', fontFamily: "'Space Grotesk', sans-serif", paddingTop: 68 }}>
      <style>{KEYFRAMES}</style>
      <ScrollProgress />

      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '0 24px' }}>

        <section
          ref={heroRef}
          onMouseMove={handleMouseMove}
          onMouseEnter={() => setMouseIn(true)}
          onMouseLeave={() => setMouseIn(false)}
          style={{ padding: '72px 0 60px', position: 'relative' }}
        >
          <div style={{
            position: 'absolute', top: -120, right: -80, width: 480, height: 480, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(56,254,96,0.055) 0%, transparent 65%)',
            animation: 'floatA 9s ease-in-out infinite', pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: -60, right: 200, width: 280, height: 280, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(56,189,248,0.04) 0%, transparent 65%)',
            animation: 'floatB 11s ease-in-out infinite', pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', top: 40, left: -60, width: 200, height: 200, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(167,139,250,0.035) 0%, transparent 65%)',
            animation: 'floatC 7s ease-in-out infinite', pointerEvents: 'none',
          }} />
          {mouseIn && (
            <div style={{
              position: 'absolute', pointerEvents: 'none', width: 560, height: 560, borderRadius: '50%',
              background: 'radial-gradient(circle at center, rgba(56,254,96,0.07) 0%, transparent 60%)',
              top: mouse.y - 280, left: mouse.x - 280,
              transition: 'top 0.12s ease, left 0.12s ease',
            }} />
          )}

          <FadeIn>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#38FE60', boxShadow: '0 0 10px #38FE60', display: 'inline-block' }} className="animate-pulse" />
              <span style={{ color: '#38FE60', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
                About Kitekraken.ai
              </span>
            </div>
            <h1 style={{ fontSize: 'clamp(3rem, 7vw, 5.2rem)', fontWeight: 800, letterSpacing: '-0.048em', lineHeight: 1.03, marginBottom: 24, maxWidth: 680 }}>
              Technology-First<br />
              <span style={{ color: '#38FE60', textShadow: '0 0 40px rgba(56,254,96,0.3)' }}>Trading Automation</span>
            </h1>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 40, alignItems: 'start' }}>
            <FadeIn delay={80}>
              <p style={{ color: '#bbb', fontSize: 17, lineHeight: 1.8, marginBottom: 18 }}>
                At Kitekraken.ai, we aren't just a trading tool — we are a <span style={{ color: '#fff', fontWeight: 600 }}>technology-first software company</span> dedicated to democratising market access through high-performance automation.
              </p>
              <p style={{ color: '#ccc', fontSize: 16, lineHeight: 1.8 }}>
                We believe the biggest obstacle for any trader isn't the market itself, but the limitations of manual execution and emotional decision-making. Our mission is to eliminate both.
              </p>
            </FadeIn>

            <FadeIn delay={140}>
              <div style={{
                borderRadius: CARD.radius, padding: '24px 28px',
                background: CARD.bg, border: `1px solid rgba(56,254,96,0.18)`,
                display: 'flex', flexDirection: 'column', gap: 14,
                boxShadow: '0 0 24px rgba(56,254,96,0.04)',
              }}>
                {[
                  'Non-custodial — your funds never leave your exchange',
                  'Live execution via TradingView webhook alerts',
                  'Backtested strategies with transparent results',
                  'Supports Crypto, Forex, Stocks & Commodities',
                ].map((pt, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#38FE60', flexShrink: 0, marginTop: 6, boxShadow: '0 0 6px #38FE60' }} />
                    <span style={{ color: '#ccc', fontSize: 14, lineHeight: 1.7 }}>{pt}</span>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        <section>
          <div ref={statsRef} style={{
            borderRadius: CARD.radius,
            border: `1px solid ${CARD.border}`,
            padding: '40px 0',
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
            gap: 0, marginBottom: 48,
            opacity: statsVis ? 1 : 0, transform: statsVis ? 'none' : 'translateY(20px)',
            transition: 'opacity 0.75s ease, transform 0.75s ease',
            background: 'linear-gradient(135deg, rgba(56,254,96,0.03) 0%, rgba(255,255,255,0.02) 50%, rgba(56,189,248,0.02) 100%)',
          }}>
            {[
              { target: 66000, suffix: '+', label: 'Active Traders',  color: '#38FE60', rgb: '56,254,96'   },
              { target: 150,   suffix: '+', label: 'Markets Covered', color: '#38bdf8', rgb: '56,189,248'  },
              { target: 99,    suffix: '%', label: 'Platform Uptime', color: '#a78bfa', rgb: '167,139,250' },
              { target: 1000,  suffix: '+', label: 'Trading Pairs',   color: '#fbbf24', rgb: '251,191,36'  },
            ].map((s, i) => (
              <div key={s.label} style={{ flex: '1 1 160px', borderRight: i < 3 ? `1px solid rgba(255,255,255,0.05)` : 'none' }}>
                <StatCounter {...s} started={statsVis} />
              </div>
            ))}
          </div>
        </section>

        <TickerStrip />

        <section style={{ marginBottom: 72 }}>
          <FadeIn>
            <div style={{ marginBottom: 40 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#38FE60', display: 'inline-block' }} />
                <span style={{ color: '#38FE60', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>Our Philosophy</span>
              </div>
              <h2 style={{ fontSize: 'clamp(2.4rem,5vw,3.6rem)', fontWeight: 800, letterSpacing: '-0.04em' }}>
                Six Principles We Build By
              </h2>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {PHILOSOPHY.map((item, i) => (
              <PhilosophyCard key={item.title} item={item} delay={i * 60} />
            ))}
          </div>
        </section>

        <section style={{ marginBottom: 72 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <FadeIn style={{ flex: '1 1 340px' }}>
              <div style={{
                borderRadius: CARD.radius, height: '100%',
                padding: '40px 36px',
                background: 'linear-gradient(145deg, rgba(56,254,96,0.05) 0%, rgba(255,255,255,0.02) 60%)',
                border: `1px solid rgba(56,254,96,0.14)`,
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,254,96,0.07) 0%, transparent 65%)', pointerEvents: 'none', animation: 'floatA 8s ease-in-out infinite' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                  <span style={{ color: '#38FE60', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>Our Vision</span>
                </div>
                <p style={{ color: '#fff', fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.5, marginBottom: 16 }}>
                  The premier software partner for the modern algorithmic trader.
                </p>
                <p style={{ color: '#ccc', fontSize: 14, lineHeight: 1.8 }}>
                  As the financial landscape evolves toward decentralisation and algorithmic precision, we are constantly innovating, refining, and expanding our integrations so our users stay ahead of an increasingly automated world.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={80} style={{ flex: '1 1 340px' }}>
              <div style={{
                borderRadius: CARD.radius, height: '100%',
                padding: '40px 36px',
                background: CARD.bg,
                border: `1px solid ${CARD.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Lock style={{ width: 15, height: 15, color: '#38bdf8' }} />
                  </div>
                  <span style={{ color: '#38bdf8', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>Transparency</span>
                </div>
                <p style={{ color: '#fff', fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.5, marginBottom: 16 }}>
                  We are a software firm — not a broker, advisor, or fund.
                </p>
                <p style={{ color: '#ccc', fontSize: 14, lineHeight: 1.8 }}>
                  Kitekraken.ai executes trades on your behalf using your own exchange credentials. We never act as financial advisors or manage your capital directly. All users are encouraged to practice responsible risk management.
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        <section style={{ marginBottom: 72 }}>
          <FadeIn>
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#38FE60', display: 'inline-block' }} />
                <span style={{ color: '#38FE60', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>Community</span>
              </div>
              <h2 style={{ fontSize: 'clamp(2.4rem,5vw,3.6rem)', fontWeight: 800, letterSpacing: '-0.04em' }}>
                Find Us Online
              </h2>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {SOCIALS.map((s, i) => <SocialCard key={s.label} s={s} delay={i * 50} />)}
          </div>
        </section>

        <section style={{ paddingBottom: 96 }}>
          <FadeIn>
            <div style={{
              borderRadius: 28,
              padding: 'clamp(40px, 6vw, 72px) clamp(32px, 5vw, 64px)',
              background: 'linear-gradient(135deg, rgba(56,254,96,0.07) 0%, rgba(255,255,255,0.02) 50%, rgba(56,189,248,0.04) 100%)',
              border: '1px solid rgba(56,254,96,0.14)',
              textAlign: 'center',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: -80, right: -80, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,254,96,0.07) 0%, transparent 70%)', pointerEvents: 'none', animation: 'floatA 10s ease-in-out infinite' }} />
              <div style={{ position: 'absolute', bottom: -60, left: -60, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,189,248,0.05) 0%, transparent 70%)', pointerEvents: 'none', animation: 'floatB 12s ease-in-out infinite' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#38FE60', boxShadow: '0 0 10px #38FE60', display: 'inline-block' }} className="animate-pulse" />
                <span style={{ color: '#38FE60', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
                  Get Started
                </span>
              </div>
              <h2 style={{ fontSize: 'clamp(2.6rem,5.5vw,4rem)', fontWeight: 800, letterSpacing: '-0.045em', marginBottom: 16, lineHeight: 1.1 }}>
                Ready to Automate<br />Your Trading Strategy?
              </h2>
              <p style={{ color: '#ccc', fontSize: 15, maxWidth: 460, margin: '0 auto 40px', lineHeight: 1.75 }}>
                Join thousands of traders leveraging Kitekraken.ai's technology-first approach to systematic trading.
              </p>
              <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => navigate('/pricing')} style={{
                  padding: '14px 36px',
                  background: '#38FE60', color: '#000',
                  border: 'none', borderRadius: 12,
                  fontSize: 13, fontWeight: 700,
                  fontFamily: "'Space Grotesk', sans-serif",
                  cursor: 'pointer', letterSpacing: '0.01em',
                  display: 'flex', alignItems: 'center', gap: 8,
                  boxShadow: '0 0 28px rgba(56,254,96,0.35)',
                  transition: 'box-shadow 0.2s, transform 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 50px rgba(56,254,96,0.6)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 28px rgba(56,254,96,0.35)'; e.currentTarget.style.transform = 'none'; }}
                >
                  Get Started
                  <ArrowRight style={{ width: 15, height: 15 }} />
                </button>
                <button onClick={() => navigate('/tutorials')} style={{
                  padding: '14px 36px',
                  background: 'transparent', color: '#bbb',
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12,
                  fontSize: 13, fontWeight: 600,
                  fontFamily: "'Space Grotesk', sans-serif",
                  cursor: 'pointer',
                  transition: 'color 0.2s, border-color 0.2s, transform 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#bbb'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.transform = 'none'; }}
                >
                  View Tutorials
                </button>
              </div>
            </div>
          </FadeIn>
        </section>

      </div>
    </div>
  );
};

export default AboutUs;
